import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import * as R from 'ramda'
import { GiphyFetch } from '@giphy/js-fetch-api'
import { Grid as SDKGrid, Gif } from '@giphy/react-components'
import { useLatest, useMeasure, useUpdateEffect } from 'react-use'

import { useMedia } from '@/utils/use-media'
import { env } from '@/config'
import { randomInt } from '@/utils/random'
import { mobile } from '@/utils/media-query'
import darkTextLoaderImg from '@/assets/images/dark_text_loader.gif'

type DisplaySize = 'small' | 'medium' | 'large'

const DEFAULT_TEXT = 'OMG!'
const GRID_SCALE_FACTOR = 4
const ALL_TEXT_STYLES_NUMBER = 42
const MEDIA_QUERY: Record<DisplaySize, string> = {
  small: mobile.query,
  medium: '(max-width: 980px)',
  large: '(min-width: 981px)',
}
const VISIBLE_TEXT_STYLES_NUMBER: Record<DisplaySize, number> = {
  large: 6,
  medium: 4,
  small: 4,
}

const RootContainer = styled.div`
  width: 677px;

  @media ${MEDIA_QUERY.medium} {
    width: 451px;
  }

  @media ${MEDIA_QUERY.small} {
    width: 338px;
  }
`

const Header = styled.div`
  background: #313131;
  border-radius: 4px 4px 0 0;
  box-shadow: inset 0px -2px 24px 0px rgba(0, 0, 0, 0.36), 0px 4px 27px 0px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  padding: 12px 15px;
  position: relative;
  width: 100%;
`

const Input = styled.input`
  background: rgba(15, 15, 15, 0.74);
  border-radius: 4px;
  border: 1px solid #444444;
  box-sizing: border-box;
  height: 48px;
  padding: 9px 126px 9px 17px;
  width: 100%;

  &,
  &::placeholder {
    color: #ffffff;
    font-family: InterFace, 'Helvetica Neue', helvetica, sans-serif;
    font-size: 20px;
    font-weight: normal;
    letter-spacing: 0;
    line-height: 26px;
  }

  &&::placeholder {
    opacity: 0.8;
  }

  &,
  &:focus {
    border: none;
    outline: none;
  }
`

const InputButton = styled.button`
  background-image: ${({ theme }) =>
    `linear-gradient(80deg, ${theme.palette.giphyIndigo} 0%, ${theme.palette.giphyGreen} 100%)`};
  background-size: 277px 79px;
  border-radius: 2px;
  bottom: 21px;
  color: #ffffff;
  font-family: PTMono-Bold;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 22px;
  position: absolute;
  right: 24px;
  text-align: center;
  top: 21px;
  width: 112px;

  &,
  &:focus {
    border: none;
    outline: none;
  }
`

const GridContainer = styled.div`
  background-color: #121212;
  border-radius: 0 0 4px 4px;
  box-shadow: inset 0 0 50px 0 rgb(0, 0, 0);
  background-image: linear-gradient(45deg, #232323 25%, transparent 25%),
    linear-gradient(-45deg, #232323 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #232323 75%),
    linear-gradient(-45deg, transparent 75%, #232323 75%);
  background-size: 40px 40px;
  background-position: 0 0, 0 20px, 20px -20px, -20px 0px;
  box-sizing: border-box;
  height: 320px;
  padding-bottom: 34px;
  padding-top: 42px;
  width: 100%;

  @media ${mobile.query} {
    height: 228px;
    padding-bottom: 20px;
    padding-top: 20px;
  }
`

const growIn = keyframes`
  0% {
    transform: scale(0.4);
  };

  100% {
    transform: scale(1);
  };
`

const Grid = styled(SDKGrid)`
  transform-origin: left top;
  transform: scale(${1 / GRID_SCALE_FACTOR});

  .${Gif.imgClassName} {
    transform: scale(0.4);

    &[style] {
      background: no-repeat center/100% url(${darkTextLoaderImg}) !important;
    }
  }

  .${Gif.imgLoadedClassName} {
    animation: ${growIn} ease-out 250ms;
    transform: scale(1);

    &[style] {
      background: none !important;
    }
  }
`

const LoaderStub = styled.div``

const gf = new GiphyFetch(env.GIPHY_API_KEY ?? '')

function useVisibleTextStylesNumber(): number {
  const matches: Record<DisplaySize, boolean> = {
    small: useMedia(MEDIA_QUERY.small),
    medium: useMedia(MEDIA_QUERY.medium),
    large: useMedia(MEDIA_QUERY.large),
  }

  const key = R.keys(matches).find((key) => matches[key])
  return key ? VISIBLE_TEXT_STYLES_NUMBER[key] : VISIBLE_TEXT_STYLES_NUMBER.large
}

function useRandomTextStyles() {
  const [text, setText] = useState('')
  const visibleStylesNumber = useVisibleTextStylesNumber()
  const visibleStylesNumberRef = useLatest(visibleStylesNumber)
  const [stylesWindow, setStylesWindow] = useState({ offset: 0, limit: visibleStylesNumber })
  const refreshStyles = useCallback(
    () =>
      setStylesWindow({
        offset: randomInt(0, ALL_TEXT_STYLES_NUMBER - visibleStylesNumberRef.current),
        limit: visibleStylesNumberRef.current,
      }),
    [visibleStylesNumberRef]
  )
  const gifsFetcher = useCallback(
    () => gf.animate(text || DEFAULT_TEXT, stylesWindow),
    [text, stylesWindow]
  )

  useUpdateEffect(() => {
    refreshStyles()
  }, [visibleStylesNumber])

  return {
    gifsFetcher,
    refreshStyles,
    setText,
    text,
    stylesWindow,
    visibleStylesNumber,
  } as const
}

export const GiphyTextPlayGround: React.FC = () => {
  const randomTextStyles = useRandomTextStyles()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [gridContainerRef, gridContainerSize] = useMeasure<HTMLDivElement>()

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus({ preventScroll: true })
    }
  }, [])

  return (
    <RootContainer>
      <Header>
        <Input
          ref={inputRef}
          autoComplete="off"
          onChange={(e) => randomTextStyles.setText(e.target.value)}
          placeholder="Type anything…"
          value={randomTextStyles.text}
          onKeyUp={(e) => {
            if (e.key === 'Enter') {
              randomTextStyles.refreshStyles()
            }
          }}
        />
        <InputButton onClick={randomTextStyles.refreshStyles}>Try Me!</InputButton>
      </Header>
      <GridContainer ref={gridContainerRef}>
        {gridContainerSize.width > 0 && (
          <Grid
            key={`${randomTextStyles.stylesWindow.offset}:${randomTextStyles.stylesWindow.limit}`}
            backgroundColor="rgba(0,0,0,0)"
            borderRadius={0}
            columns={randomTextStyles.visibleStylesNumber / 2}
            fetchGifs={randomTextStyles.gifsFetcher}
            gutter={125}
            hideAttribution={true}
            loader={LoaderStub}
            noLink={true}
            width={gridContainerSize.width * GRID_SCALE_FACTOR}
          />
        )}
      </GridContainer>
    </RootContainer>
  )
}
